/**
 * MFA Service
 * API Layer - Handles all MFA-related API calls
 * Uses temporary MFA token (NOT JWT) for two-step authentication
 * Location: components/MFA/MFAService.ts
 */

import { BASE_URL } from "../../App_Settings/app_settings";

/**
 * Get MFA token and userId from sessionStorage (set during login)
 */
const getMfaCredentials = (): { mfaToken: string; userId: string } => {
  return {
    mfaToken: sessionStorage.getItem("mfaToken") || "",
    userId: sessionStorage.getItem("mfaUserId") || "",
  };
};

/**
 * Resend MFA OTP code using temporary MFA token
 * @param userId - The user ID (unused, read from sessionStorage)
 * @param method - Delivery method (unused, backend determines from user settings)
 */
export const sendMFACodeAPI = async (userId: number, method: string): Promise<boolean> => {
  try {
    const { mfaToken, userId: mfaUserId } = getMfaCredentials();
    if (!mfaToken || !mfaUserId) {
      throw new Error("MFA session expired. Please login again.");
    }

    const response = await fetch(`${BASE_URL}/Users/resendotp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ mfaToken, userId: mfaUserId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Surface rate limit details so the UI can sync its cooldown timer
      const err: any = new Error(errorData.message || "Failed to send MFA code");
      err.errorCode = errorData.errorCode;
      err.retryAfterSeconds = errorData.retryAfterSeconds;
      err.retryAfterMinutes = errorData.retryAfterMinutes;
      err.status = response.status;
      throw err;
    }

    return true;
  } catch (error) {
    console.error("Error sending MFA code:", error);
    throw error;
  }
};

/**
 * Verify MFA OTP code using temporary MFA token
 * On success, returns the real JWT + session data from server
 * @param otp - The 6-digit OTP code
 */
export const verifyMFACodeAPI = async (
  otp: string
): Promise<{
  success: boolean;
  message?: string;
  data?: any;
  errorCode?: string;
  retryAfterMinutes?: number;
}> => {
  try {
    const { mfaToken, userId } = getMfaCredentials();
    if (!mfaToken || !userId) {
      return {
        success: false,
        message: "MFA session expired. Please login again.",
      };
    }

    const response = await fetch(`${BASE_URL}/Users/verifyotp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ otp, mfaToken, userId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || "Failed to verify OTP",
        errorCode: errorData.errorCode,
        retryAfterMinutes: errorData.retryAfterMinutes,
      };
    }

    // Server returns the full AuthenticateUser response with real JWT
    const data = await response.json().catch(() => ({}));

    // Store the real JWT and session token now that MFA is verified
    if (data.token) {
      localStorage.setItem("token", JSON.stringify(data));
      if (data.sessionToken) {
        localStorage.setItem("sessionToken", data.sessionToken);
      }
    }

    return {
      success: true,
      message: "OTP verified successfully",
      data,
    };
  } catch (error) {
    console.error("Error verifying OTP:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Network error occurred",
    };
  }
};

/**
 * Resend MFA OTP code
 */
export const resendMFACodeAPI = async (userId: number, method: string): Promise<boolean> => {
  return sendMFACodeAPI(userId, method);
};
